// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canSubmitRequisition,
    getCurrentCartState,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import FancyScroll, { FancyScrollRefProps } from "@synd/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@synd/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@synd/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@synd/App/Components/Notify/Notify";
import Skeleton from "@synd/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { getSettingsCollection } from "@synd/App/Store/Selectors/SettingsSelectors";
import { pluralize } from "@synd/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const isCheckoutDisabled = isCartCheckoutDisabled(cart);

    return {
        isCheckoutDisabled,
        canSubmitRequisition: canSubmitRequisition(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        cart,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isLoading: state.data.carts.isLoading,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        onePageCheckout: getSettingsCollection(state).cartSettings.onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        showPricing: state.context.showPricing,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
        isClearingCart: state.pages.cart.isClearingCart,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
    signInAsGuest,
    clearCurrentCart,
};
interface OwnProps {
    closePopup?: () => void;
    isPopupShown?: boolean;
}
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    centerWrapper?: InjectableCss;
    title?: TypographyProps;
    noCartLinesNotify?: TypographyProps;
    minicartFooterContainer?: InjectableCss;
    summarySectionWrapper?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    countLabel?: TypographyProps;
    countValue?: TypographyProps;
    subTotalLabel?: TypographyProps;
    subTotalValue?: TypographyProps;
    actionWrapper?: InjectableCss;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    wrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    fancyScroll?: InjectableCss;
    clearCartWrapper?: InjectableCss;
    clearCartButton?: LinkPresentationProps;
    removeAllModal?: TwoButtonModalStyles;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    signInAsGuest,
    clearCurrentCart,
    cart,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isPunchOutOrder,
    isCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    canSubmitRequisition,
    isUpdatingCartLine,
    isRemovingCartLine,
    isLoading,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    onePageCheckoutPageUrl,
    hasQuoteRequiredProducts,
    onePageCheckout,
    rememberMe,
    showPricing,
    enableVat,
    vatPriceDisplay,
    isClearingCart,
    closePopup,
    isPopupShown,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);
        setIsCheckoutLoading(true);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const clearCartClickHandler = () => {
        clearCurrentCart();
        setIsRemoveModalOpen(false);
    };

    const openClearCartConfirmation = () => {
        setIsRemoveModalOpen(true);
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        history.push(cartPageUrl);
        closePopup?.();
    };

    // XNG-Change: use Skeleton
    if (!cart || isLoading[cart.id]) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <Skeleton height="100px" />
                <Skeleton height="100px" />
                <Skeleton height="100px" />
                <Skeleton height="100px" />
                <Skeleton height="100px" />
                <Skeleton height="100px" />
            </StyledWrapper>
        );
    }

    const cartLines = cart.cartLines ?? [];
    const totalQtyOrdered = cartLines.reduce((sum, line) => sum + (line.qtyOrdered || 0), 0);
    const isLoadingCart = isLoading[cart.id];
    const isCheckoutButtonDisabled = isCheckoutDisabled || isCheckoutLoading || isLoadingCart;

    return (
        <>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={isLoading[cart.id] || isUpdatingCartLine || isRemovingCartLine || isClearingCart}
            >
                <StyledWrapper {...styles.wrapper}>
                    {cartLines.length > 0 ? (
                        <>
                            <StyledWrapper {...styles.clearCartWrapper}>
                                <Link
                                    {...styles.clearCartButton}
                                    onClick={openClearCartConfirmation}
                                    data-test-selector="miniCart_clearCart"
                                >
                                    {translate("Clear Cart")}
                                </Link>
                            </StyledWrapper>
                            <FancyScroll extendedStyles={styles.fancyScroll} refElement={parentBoxRef} ref={fancyScrollRef}>
                                <StyledWrapper
                                    {...styles.cartLinesWrapper}
                                    data-test-selector="cartLines-container"
                                    ref={parentBoxRef}
                                >
                                    {cartLines.map(cartLine => {
                                        const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                        return (
                                            <MiniCartLineCard
                                                key={`miniCartLine_${cartLine.id}`}
                                                cartLine={cartLine}
                                                cart={cart}
                                                qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                                removeCartLineClickHandler={removeCartLineClickHandler}
                                                errorMessages={errorMessages}
                                                showPricing={showPricing}
                                                enableVat={enableVat}
                                                vatPriceDisplay={vatPriceDisplay}
                                            />
                                        );
                                    })}
                                </StyledWrapper>
                            </FancyScroll>
                        </>
                    ) : (
                        <StyledWrapper {...styles.centerWrapper}>
                            <Notify {...styles.noCartLinesNotify} type="info">
                                {siteMessage("Cart_NoOrderLines")}
                            </Notify>
                        </StyledWrapper>
                    )}

                    <StyledWrapper {...styles.minicartFooterContainer} data-test-selector="miniCart-footer-container">
                        <StyledWrapper {...styles.summarySectionWrapper}>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.countLabel}>
                                    {pluralize(totalCountDisplay, translate("Item"))}:
                                </Typography>
                                <Typography {...styles.countValue}>{totalCountDisplay}</Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.subTotalLabel}>{translate("Subtotal")}:</Typography>
                                <Typography {...styles.subTotalValue}>{orderSubTotalDisplay}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.infoWrapper}>
                            <Typography {...styles.countLabel}>{translate("Cases")}:</Typography>
                            <Typography {...styles.countValue}>{totalQtyOrdered}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.actionWrapper}>
                            <Button
                                {...styles.viewCartButton}
                                aria-label={translate("View Cart")}
                                onClick={handleViewCarClick}
                            >
                                {translate("View Cart")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </LoadingOverlay>
            <TwoButtonModal
                headlineText={siteMessage("Custom_Cart_RemoveAllCartLinesTitle")}
                modalIsOpen={isRemoveModalOpen}
                messageText={siteMessage("Custom_Cart_RemoveAllCartLinesMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={() => setIsRemoveModalOpen(false)}
                onSubmit={clearCartClickHandler}
                submitTestSelector="miniCart_clearCartSubmit"
                dataTestSelector="twoButtonModal_MiniCartClearCart"
                extendedStyles={styles.removeAllModal}
            />
        </>
    );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
